import { ChevronDown, ChevronUp, X } from "lucide-react";

import { requiredWarning } from "@/lib/attributeReview/requiredWarning";

type Props = {
  positionText: string;
  label: string;
  description: string | null;
  requirementLevel: unknown;
  storeLine: string;
  hasPrev: boolean;
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
};

const iconButton =
  "inline-flex h-7 w-7 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-600 transition-transform duration-100 ease-out hover:bg-slate-100 active:scale-[0.97] focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-40 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800";

export function ReviewDrawerHeader({
  positionText,
  label,
  description,
  requirementLevel,
  storeLine,
  hasPrev,
  hasNext,
  onPrev,
  onNext,
  onClose,
}: Props) {
  const warning = requiredWarning(requirementLevel);
  return (
    <header className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-200 px-[18px] pb-3.5 pt-4 dark:border-slate-800">
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex flex-wrap items-baseline gap-2">
          <h2 className="break-words font-heading text-[22px] font-semibold leading-tight text-slate-950 [overflow-wrap:anywhere] dark:text-slate-50">
            {label}
          </h2>
          {warning && (
            <span
              title={warning.message}
              className={
                warning.required
                  ? "text-xs font-semibold text-rose-600 dark:text-rose-300"
                  : "text-xs font-semibold text-amber-600 dark:text-amber-300"
              }
            >
              {warning.required ? "Required" : "Sometimes required"}
            </span>
          )}
        </div>
        <p className="text-[13px] text-slate-500 dark:text-slate-400">
          {storeLine}
          {positionText && ` · ${positionText}`}
        </p>
        {description && (
          <p className="text-[13px] text-slate-500 dark:text-slate-400">
            {description}
          </p>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          aria-label="Previous field"
          title="Previous field (K)"
          disabled={!hasPrev}
          onClick={onPrev}
          className={iconButton}
        >
          <ChevronUp className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          aria-label="Next field"
          title="Next field (J)"
          disabled={!hasNext}
          onClick={onNext}
          className={iconButton}
        >
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          aria-label="Close"
          title="Close (Esc)"
          onClick={onClose}
          className={iconButton}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </header>
  );
}
